import { useEffect, useRef, useState } from 'react';
import { GridRenderEditCellParams, useGridApiContext } from '@mui/x-data-grid-premium';
import { formatNumber, parseLocaleNumber } from '@/lib/locale';
import { fitsScale } from '@/lib/decimalScale';

/**
 * Locale-aware number edit cell for a DataGrid column. Uses a plain text input (so the user can type the
 * locale decimal separator — e.g. `,` in sv — regardless of the browser locale) and parses the
 * typed string to a JS number via the active format locale on every change. The cell value stored
 * in the grid is always a real number, so downstream derivation/totals keep working. Its own text
 * state is never re-synced from the parsed value, so the comma the user types never flips to a dot.
 *
 * Two keyboard sub-states (spreadsheet-style):
 *  - "enter": just arrowed in — value selected, arrows jump cell-to-cell, typing overwrites.
 *  - "caret": entered via F2 (always) or Enter (on an untouched cell) — caret sits in the value,
 *    so ←/→/Home/End move within the number instead of jumping cells. Enter after typing, or in
 *    caret mode, commits and moves on (MUI default).
 *
 * `scale` is the decimal places the column's value is stored with (Plan Entry: storedScale): a keystroke
 * that would give the number a decimal more is refused and the cell keeps what it had, so what it shows
 * and derives is what the server stores. The text opens at that scale too: a weighted Total (Σ(Pris ×
 * Volym) / Σ Volym) carries every digit, but the cell offers what can be typed and stored. Null takes any
 * number.
 */
export function LocaleNumberEditCell(props: GridRenderEditCellParams & { scale: number | null }) {
  const { id, field, value, hasFocus, scale } = props;
  const apiRef = useGridApiContext();
  const inputRef = useRef<HTMLInputElement>(null);
  const [caretMode, setCaretMode] = useState(false);
  const dirtyRef = useRef(false);
  const [text, setText] = useState<string>(() =>
    typeof value === 'number' && Number.isFinite(value)
      ? formatNumber(value, { useGrouping: false, maximumFractionDigits: scale ?? 20 })
      : '',
  );

  useEffect(() => {
    if (hasFocus) {
      const el = inputRef.current;
      el?.focus();
      // Select the whole value so arrowing into a cell shows it highlighted and the first
      // keystroke overwrites it (spreadsheet "enter" behaviour).
      if (!caretMode) el?.select();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFocus]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const n = parseLocaleNumber(raw);
    if (!Number.isNaN(n) && !fitsScale(n, scale)) return;
    dirtyRef.current = true;
    setText(raw);
    // A blank/unparseable input commits as '' — the numeric column's valueParser maps '' to null
    // (MUI's cleared-cell convention), which the row model reads as a blank cell. Committing
    // `undefined` instead would be parsed as Number(undefined) = NaN, so blanking a cell would
    // leave NaN in the row instead of a proper blank.
    apiRef.current.setEditCellValue({ id, field, value: Number.isNaN(n) ? '' : n });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // F2 (always) or Enter (on an untouched cell) → enter caret edit mode: drop the selection and
    // place the caret at the end so ←/→ edit within the number.
    if (e.key === 'F2' || (e.key === 'Enter' && !caretMode && !dirtyRef.current)) {
      e.preventDefault();
      e.stopPropagation();
      setCaretMode(true);
      const el = inputRef.current;
      if (el) {
        const len = el.value.length;
        el.setSelectionRange(len, len);
      }
      return;
    }
    // In caret mode, keep horizontal arrows / Home / End inside the input (don't let the grid's
    // onCellKeyDown jump to the next cell).
    if (caretMode && (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Home' || e.key === 'End')) {
      e.stopPropagation();
    }
  };

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="decimal"
      value={text}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      style={{
        width: '100%',
        height: '100%',
        textAlign: 'right',
        border: 'none',
        outline: 'none',
        padding: '0 10px',
        fontSize: 'inherit',
        fontFamily: 'inherit',
        backgroundColor: 'transparent',
        boxSizing: 'border-box',
      }}
    />
  );
}
